import { IDataRequest, IResearchTeamMemberDetail } from '@vivli/features/data-requests/infrastructure/interface';
import { useActiveUserContext, useDataRequestPermissionsContext } from '@vivli/core/infrastructure/context';
import { useEffect, useState } from 'react';
import {
    DataRequestStatusEnum,
    ResearchTeamMemberAccessStatusEnum,
    ResearchTeamMemberAccountStatusEnum,
} from '@vivli/features/data-requests/infrastructure/enum';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

export function useResearchTeamPermissions(dataRequest: IDataRequest, isAdminEditing: boolean = false) {
    const [userCanEdit, setUserCanEdit] = useState(false);
    const [userCanDelete, setUserCanDelete] = useState(false);
    const [userCanManage, setUserCanManage] = useState(false);
    const { userIsOwner, userIsAdmin } = useDataRequestPermissionsContext();
    const { user, hasPermission } = useActiveUserContext();

    const handlePermissions = () => {
        const inDraft = dataRequest.status === DataRequestStatusEnum.Draft;
        const isDrOwnerOrAdmin = userIsOwner(dataRequest) || userIsAdmin(dataRequest);
        //user is Admin, or an owner if there are no admin
        const userIsAdminInDraft =
            inDraft &&
            (userIsAdmin(dataRequest) ||
                !(dataRequest.researchTeam?.researchTeamDetails?.some((tm) => tm.isAdmin) && userIsOwner(dataRequest)));

        // DEV-586: the Lead Researcher may see/edit the Research Team tab in Draft with the same
        // capability as the Request Admin (add/remove/grant), scoped to Draft.
        const userIsLead = dataRequest.researchTeam?.researchTeamDetails?.some(
            (tm) => tm.isLeadResearcher && tm.userId === user?.userId
        );
        const isLeadInDraft = inDraft && userIsLead;

        //TODO PERMFE - redo with permissions+
        const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);
        setUserCanEdit((isVivliAdmin && isAdminEditing) || (inDraft && isDrOwnerOrAdmin) || isLeadInDraft);
        setUserCanManage(isVivliAdmin || isDrOwnerOrAdmin || isLeadInDraft);
        setUserCanDelete(isVivliAdmin || userIsAdminInDraft || isLeadInDraft);
    };

    useEffect(() => {
        handlePermissions();
    }, [dataRequest, user, isAdminEditing]);

    // A member may RECEIVE the RequestAdmin role only if they are a non-admin who already has access
    // to the data request and is on an Enabled Vivli account (RequestAdmin must be Active/Enabled — DEV-510).
    const canTransferAdminRole = (teamMember: IResearchTeamMemberDetail): boolean =>
        !teamMember.isAdmin &&
        teamMember.accessToDataRequest === ResearchTeamMemberAccessStatusEnum.Granted &&
        teamMember.statusDto?.account === ResearchTeamMemberAccountStatusEnum.Enabled;

    return {
        userCanEdit,
        userCanManage,
        userCanDelete,
        canTransferAdminRole,
    };
}
